import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import HeroTransition from '../components/HeroTransition';
import useNavigationWarning from '../hooks/useNavigationWarning';
import { logActivity } from '../utils/sessionManager';
import './SuperheroSelect.css';

// Import hero images
import blackwidow from '../assets/heroesForUser/blackwidow.png';
import captainmarvel from '../assets/heroesForUser/captainmarvel.png';
import flash from '../assets/heroesForUser/flash_1.png';
import harley from '../assets/heroesForUser/harley.png';
import scarletwitch from '../assets/heroesForUser/scarletwitch.png';
import shazam from '../assets/heroesForUser/shazam.png';
import shehulk from '../assets/heroesForUser/shehulk.png';
import spiderman from '../assets/heroesForUser/spiderman_1.png';
import superman from '../assets/heroesForUser/superman_1.png';
import thor from '../assets/heroesForUser/thor.png';
import wonderwoman from '../assets/heroesForUser/wonderwoman.png';

const SuperheroSelect = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { transition, email } = location.state || {};

  const hasLoggedPageVisit = useRef(false);

  // Add navigation warning with modal
  const { NavigationWarningModal } = useNavigationWarning(true);

  // Log page visit only once
  useEffect(() => {
    if (!hasLoggedPageVisit.current) {
      logActivity('superhero_select_visited', { transition });
      hasLoggedPageVisit.current = true;
    }
  }, [transition]);

  const superheroes = [
    { id: 1, name: 'The Elite Spy', color: '#DC143C', image: blackwidow, description: 'Master tactical agent with enhanced agility and combat skills.' },
    { id: 2, name: 'The Cosmic Commander', color: '#FF4500', image: captainmarvel, description: 'Cosmic-powered guardian with super strength and energy manipulation.' },
    { id: 3, name: 'The Speedster', color: '#FFD700', image: flash, description: 'Futuristic runner with super speed and time-bending reflexes.' },
    { id: 4, name: 'The Web Acrobat', color: '#FF6347', image: spiderman, description: 'Agile city defender with acrobatic skills and technical gadgets.' },
    { id: 5, name: 'The Reality Weaver', color: '#8B0000', image: scarletwitch, description: 'Mystical sorceress wielding powerful chaos energy.' },
    { id: 6, name: 'The Solar Paragon', color: '#1E90FF', image: superman, description: 'Invulnerable hero with super strength, flight, and heat vision.' },
    { id: 7, name: 'The Storm Warrior', color: '#4169E1', image: thor, description: 'Fierce warrior in silver-plated armor wielding a large metallic club.' },
    { id: 8, name: 'The Warrior Queen', color: '#FF1493', image: wonderwoman, description: 'Mighty warrior in ornate gold and crimson armor with a jeweled headband.' },
    { id: 9, name: 'The Gamma Amazon', color: '#32CD32', image: shehulk, description: 'Powerhouse hero with incredible green-skinned strength and durability.' },
    { id: 10, name: 'The Chaos Jester', color: '#FF69B4', image: harley, description: 'Unpredictable acrobat with a chaotic nature and batting skills.' },
    { id: 11, name: 'The Thunder Champion', color: '#FF0000', image: shazam, description: 'Magical champion who transforms with a bolt of lightning.' },
    { id: 12, name: 'Random', color: '#FFDC00', image: null, description: 'Surprise hero selection!' },
];

  const handleSelectHero = (hero) => {
    logActivity('superhero_selected', { superhero: hero.name });

    // Auto-navigate to next page immediately after selection
    let heroToPass = hero;
    if (hero.id === 12) {
      // Random selection from the first 11 heroes
      const randomIndex = Math.floor(Math.random() * 11);
      heroToPass = superheroes[randomIndex];
      logActivity('random_superhero_selected', { superhero: heroToPass.name });
    }

    // Generate transition for next page
    const transitions = ['superman', 'flash'];
    const nextTransition = transitions[Math.floor(Math.random() * transitions.length)];

    logActivity('continuing_to_photo_capture', { superhero: heroToPass.name });
    navigate('/capture-photo', { state: { transition: nextTransition, superhero: heroToPass, email } });
  };

  const handleHomeClick = () => {
    logActivity('navigation_home_from_superhero_select');
    navigate('/', { replace: true });
  };

  const content = (
    <div className="superhero-select">
      <div className="superhero-background"></div>

      {/* Render the navigation warning modal */}
      {NavigationWarningModal}

      <motion.button
        className="home-button"
        onClick={handleHomeClick}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: transition ? 0.5 : 0 }}
      >
        HOME
      </motion.button>

      <div className="content-container">
        <motion.h1
          className="page-title"
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: transition ? 0.5 : 0 }}
        >
          CHOOSE YOUR SUPERHERO
        </motion.h1>

        <div className="heroes-grid">
          {superheroes.map((hero, index) => (
            <motion.div
              key={hero.id}
              className="hero-card"
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: (transition ? 0.5 : 0) + index * 0.1, duration: 0.4 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => handleSelectHero(hero)}
              style={{ borderColor: hero.color }}
            >
              <div className="hero-image">
                {hero.image ? (
                  <img src={hero.image} alt={hero.name} className="hero-icon" />
                ) : (
                  <div className="random-placeholder">?</div>
                )}
                <h3 className="hero-name">{hero.name}</h3>
              </div>
            </motion.div>
          ))}
        </div>


      </div>
    </div>
  );

  if (transition) {
    return (
      <HeroTransition transitionType={transition}>
        {content}
      </HeroTransition>
    );
  }

  return content;
};

export default SuperheroSelect;
